import useTranslation from 'next-translate/useTranslation'
import { FC, useState } from 'react'
import { FlexboxGrid } from 'rsuite'

import { Product } from '../../utils/types/Product'
import PackagingIcon from '../Icons/PackagingIcon'
import { getPerBoxItems, getPerCartonItems, getSalesUnit, isPairProduct } from '../../lib/salesUnit'
import { formatGenericIntegerNumber } from '../../utils/math'
import SalesUnitSizes from './SalesUnitSizes'
import ExpandArrowIcon from '../Icons/ExpandArrowIcon'

import styles from '../../styles/PackagingDetailsSPP.module.less'

interface PackagingDetailsSPPProps {
  product: Product
}

const PackagingDetailsSPP: FC<PackagingDetailsSPPProps> = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const { t } = useTranslation('products')
  const perPackage = getPerBoxItems(product)
  const perCarton = getPerCartonItems(product)
  const isPair = isPairProduct(product)
  const unitTypeTranslationOne = isPair ? t('pair_one') : t('piece_one')
  const unitTypeTranslationOther = isPair ? t('pair_other') : t('piece_other')
  const salesUnit = getSalesUnit(product)

  const getPackagingDetailsText = () => {
    if (perCarton
      && salesUnit === 'carton' // Always match sales unit sizes with actual sales unit – so only show carton if sales unit is carton
    ) {
      return `${t('A carton contains')} ${perCarton > 1 ? `${perCarton} ${t('package_other')}` : `${perCarton} ${t('package_one')}`}
       (${formatGenericIntegerNumber(perCarton * perPackage)} ${
  perCarton * perPackage > 1 ? unitTypeTranslationOther : unitTypeTranslationOne})`
    }

    if (perPackage
      && salesUnit === 'package' // Always match sales unit sizes with actual sales unit – so only show package if sales unit is package
    ) {
      return t('A package contains {{ perPackageItems }} {{ unit }}',
        {
          perPackageItems: perPackage,
          unit: perPackage > 1 ? unitTypeTranslationOther : unitTypeTranslationOne,
        })
    }

    if (isPair) {
      return t('Sold in units of one pair')
    }

    return t('Sold in units of one piece')
  }

  return (
    <>
      <FlexboxGrid className="margin-top-spacer flex-flow-row">
        <FlexboxGrid.Item className={styles['packaging-icon-container']}>
          <PackagingIcon />
        </FlexboxGrid.Item>
        <FlexboxGrid.Item className="margin-left-spacer flex-shrink-col-dynamic">
          <p>{getPackagingDetailsText()}</p>
          <FlexboxGrid
            align="middle"
            className="cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <b>{t('View packaging details')}</b>
            <ExpandArrowIcon className={`${styles['arrow-icon']} ${isExpanded ? styles.expand : ''}`} />
          </FlexboxGrid>
        </FlexboxGrid.Item>
      </FlexboxGrid>
      {isExpanded && (
        <SalesUnitSizes
          product={product}
          className="margin-top-spacer-double"
        />
      )}
    </>
  )
}

export default PackagingDetailsSPP
